import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import toast from "react-hot-toast";
import { HiOutlineArrowLeft } from "react-icons/hi";
import * as Yup from "yup";
import { FloatingInput, Loader } from "../../Components/Common";
import * as routes from "../../routes";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";
import {
  LEAVE_CATEGORY_OPTIONS,
  LEAVE_REQUEST_MODE,
  LEAVE_REQUEST_STATUS,
  LEAVE_REQUEST_VALIDATION_MESSAGES,
  TOAST_MESSAGES,
} from "./constants";
import {
  approveLeaves,
  createLeaveRequest,
  getLeaveRequestDetailsById,
  getLeaveSummary,
  updateLeaveRequest,
} from "../../Services/LeaveServices";
import { formatDateDDMMYYYY, toProperCase } from "../../Utils";
import { UserContext } from "../../Context";
import { usePayroll } from "../PayRoll/Hooks/usePayroll";

const LeaveRequestActions = () => {
  const { leaveRequestId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useContext(UserContext);
  const { fetchSalarySlips } = usePayroll();
  const [mode, setMode] = useState(LEAVE_REQUEST_MODE.ADD);
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isSalaryCalculated, setIsSalaryCalculated] = useState(false);
  const [salaryData, setSalaryData] = useState("");
  const today = new Date().toISOString().split("T")[0];
  const [formData, setFormData] = useState({
    start_date: today,
    end_date: "",
    category: "",
  });

  const [summaryData, setSummaryData] = useState("");

  const isViewMode = mode === LEAVE_REQUEST_MODE.VIEW;
  const filters = location.state?.filters || {
    status: "",
    category: "",
    user: "",
  };

  //leave type state
  const [isSingleLeaveDay, setIsSingleLeaveDay] = useState(true);
  const [isHalfDayLeave, setIsHalfDayLeave] = useState(false);
  const isSingle = initialValues?.start_date === initialValues?.end_date;
  const isHalf = initialValues?.is_half_day;

  const fromPage = location.state?.fromPage || routes.LEAVE_REQUEST_LIST;

  useEffect(() => {
    if (location.pathname.includes("view")) setMode(LEAVE_REQUEST_MODE.VIEW);
    else setMode(LEAVE_REQUEST_MODE.ADD);
  }, [location.pathname]);

  useEffect(() => {
    if (mode === LEAVE_REQUEST_MODE.VIEW) {
      const id = location.pathname.split("/").pop();
      setLoading(true);
      getLeaveRequestDetailsById(id)
        .then((res) => {
          setInitialValues(res.data);
          setIsSingleLeaveDay(res.data.start_date === res.data.end_date);
        })
        .catch(() => toast.error(TOAST_MESSAGES.FAILED_TO_FETCH_DETAILS))
        .finally(() => setLoading(false));
    } else {
      setInitialValues({
        start_date: today,
        end_date: today,
        category: "",
        reason: "",
        is_half_day: false,
      });
    }
  }, [mode, location.pathname]);

  const checkSalarySlip = async () => {
    const salary_user =
      mode === LEAVE_REQUEST_MODE.VIEW ? initialValues?.user : user.id;
    const start = formData.start_date || initialValues?.start_date;
    const end = formData.end_date || initialValues?.end_date;

    if (start && end && user?.id) {
      const payload = {
        user: salary_user,
        start_date: start,
        end_date: end,
      };

      const data = await fetchSalarySlips(payload);
      if (data) {
        setIsSalaryCalculated(true);
        setSalaryData(data);
      } else {
        setIsSalaryCalculated(false);
      }
    }
  };

  const getSummaries = async () => {
    const summary_user =
      mode === LEAVE_REQUEST_MODE.VIEW ? initialValues?.user : user.id;
    const start = formData.start_date || initialValues?.start_date;
    const end = formData.end_date || initialValues?.end_date;
    const category = formData.category || initialValues?.category;

    if (start && end && user?.id && category) {
      const payload = {
        user: summary_user,
        start_date: start,
        end_date: end,
        category: category,
      };

      try {
        const response = await getLeaveSummary(payload);
        if (response) {
          setSummaryData(response.data);
        } else {
          const msg =
            extractErrorMessage(response) ||
            TOAST_MESSAGES.FAILED_FETCH_SUMMARY;
          toast.error(msg);
        }
      } catch (error) {
        const msg =
          extractErrorMessage(error) || TOAST_MESSAGES.FAILED_FETCH_SUMMARY;
        toast.error(msg);
      }
    }
  };

  useEffect(() => {
    checkSalarySlip();
  }, [formData.start_date, formData.end_date, initialValues, mode]);

  useEffect(() => {
    getSummaries();
  }, [
    formData.start_date,
    formData.end_date,
    formData.category,
    initialValues,
    mode,
  ]);

  const validationSchema = Yup.object().shape({
    start_date: Yup.date()
      .required(LEAVE_REQUEST_VALIDATION_MESSAGES.START_DATE)
      .test(
        "block-previous-month-after-5th",
        "Leave for previous months cannot be applied after the 5th of current month.",
        function (value) {
          if (!value) return false;

          const today = new Date();
          const selectedDate = new Date(value);

          const isAfter5th = today.getDate() > 5;

          const isCurrentMonth =
            selectedDate.getMonth() === today.getMonth() &&
            selectedDate.getFullYear() === today.getFullYear();

          if (isAfter5th && !isCurrentMonth) {
            return false;
          }

          return true;
        },
      ),

    end_date: Yup.date().required(LEAVE_REQUEST_VALIDATION_MESSAGES.END_DATE),

    category: Yup.string().required(LEAVE_REQUEST_VALIDATION_MESSAGES.CATEGORY),

    reason: Yup.string().required(LEAVE_REQUEST_VALIDATION_MESSAGES.REASON),
  });
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const formData = new FormData();
      formData.append("start_date", values.start_date);
      formData.append("end_date", values.end_date);
      formData.append("category", values.category);
      formData.append("reason", values.reason);
      formData.append("is_half_day", values.is_half_day);

      const response = await createLeaveRequest(formData);

      if (response?.status === 201 || response?.status === 200) {
        toast.success(TOAST_MESSAGES.ADD_LEAVE);
        navigate(routes.LEAVE_REQUEST_LIST);
      } else {
        const msg =
          extractErrorMessage(response) || TOAST_MESSAGES.FAILED_ADD_LEAVE;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || TOAST_MESSAGES.FAILED_ADD_LEAVE;
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      let response;

      if (
        status === LEAVE_REQUEST_STATUS.APPROVED ||
        status === LEAVE_REQUEST_STATUS.PENDING_L2_APPROVAL ||
        status === LEAVE_REQUEST_STATUS.REJECTED
      ) {
        response = await approveLeaves(leaveRequestId, { status });
      } else if (status === LEAVE_REQUEST_STATUS.CANCELLED) {
        response = await updateLeaveRequest(leaveRequestId, { status });
      }

      if (response?.status === 200) {
        toast.success(
          status === LEAVE_REQUEST_STATUS.CANCELLED
            ? TOAST_MESSAGES.LEAVE_UPDATE_SUCCESS
            : status === LEAVE_REQUEST_STATUS.APPROVED
              ? TOAST_MESSAGES.APPROVAL_SUCCESS
              : TOAST_MESSAGES.LEAVE_REJECTED,
        );
        navigate(fromPage);
      } else {
        const msg =
          extractErrorMessage(response) || TOAST_MESSAGES.LEAVE_UPDATE_FAILED;
        toast.error(msg);
      }
    } catch (error) {
      const msg =
        extractErrorMessage(error) || TOAST_MESSAGES.LEAVE_UPDATE_FAILED;
      toast.error(msg);
    }
  };

  const handleBack = () => {
    navigate(fromPage, { state: { filters } });
  };

  if (loading || !initialValues)
    return (
      <div className="flex justify-center pt-10">
        <Loader size={4} />
      </div>
    );

  const commonInput =
    "w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-secondary";

  return (
    <div className="w-full max-w-xl mx-auto bg-white p-6 md:p-8 rounded-lg shadow-md mt-8">
      <div className="flex flex-col items-center mb-6">
        <div className="flex items-center justify-between w-full">
          <button onClick={handleBack} className="text-primary text-2xl">
            <HiOutlineArrowLeft />
          </button>
          <h2 className="text-lg font-bold text-primary text-center flex-1">
            {isViewMode ? "Leave Details" : "Add Leave"}
          </h2>
          <div className="w-6" />
        </div>

        <div className="flex gap-2 mt-2 flex-wrap justify-center">
          {/* Salary Status Tag */}
          {isSalaryCalculated && salaryData?.status ? (
            <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded-full">
              Salary Calculated - {toProperCase(salaryData?.status || "")}
            </span>
          ) : (
            <span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-2 py-1 rounded-full">
              Salary Not Calculated
            </span>
          )}

          {/* LOP Tag */}
          {summaryData?.lop_applicable && (
            <span className="bg-red-100 text-red-800 text-xs font-semibold px-2 py-1 rounded-full">
              LOP Applicable
            </span>
          )}

          {/* Paid Days Info */}
          {summaryData &&
            !summaryData?.lop_applicable &&
            summaryData?.paid_days !== undefined && (
              <span className="bg-green-100 text-green-800 text-xs font-semibold px-2 py-1 rounded-full">
                Paid Days - {summaryData?.paid_days || 0}
              </span>
            )}
        </div>
      </div>

      <Formik
        initialValues={initialValues}
        validationSchema={isViewMode ? null : validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({ isSubmitting, setFieldValue, values, isValid, dirty }) => (
          <Form className="flex flex-col gap-1">
            {/* Add Mode */}
            {!isViewMode && (
              <>
                {/* Category */}
                <div className="">
                  <label className="text-sm font-semibold text-secondary mb-1 block">
                    {"Category"}
                  </label>

                  <Field
                    as="select"
                    name="category"
                    className={commonInput}
                    disabled={
                      isSubmitting || LEAVE_CATEGORY_OPTIONS.length === 0
                    }
                    onChange={(e) => {
                      setFieldValue("category", e.target.value);
                      setFormData((prev) => ({
                        ...prev,
                        category: e.target.value,
                      }));
                    }}
                  >
                    <option value="">
                      {LEAVE_CATEGORY_OPTIONS.length === 0
                        ? "No Category available"
                        : "Select Category"}
                    </option>
                    {LEAVE_CATEGORY_OPTIONS.map((cat) => (
                      <option key={cat.value} value={cat.value}>
                        {cat.label}
                      </option>
                    ))}
                  </Field>
                  <ErrorMessage
                    name="category"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
                <div className="flex gap-6 m-2">
                  {/* Half Day */}
                  <label>
                    <input
                      type="radio"
                      name="leaveType"
                      className="mr-2 text-secondary"
                      checked={isHalfDayLeave}
                      onChange={() => {
                        setIsHalfDayLeave(true);
                        setIsSingleLeaveDay(true);
                        setFieldValue("is_half_day", true);
                        setFieldValue("end_date", values.start_date);
                      }}
                    />
                    Half Day
                  </label>
                  {/* Single Day */}
                  <label>
                    <input
                      type="radio"
                      name="leaveType"
                      className="mr-2 text-secondary"
                      checked={isSingleLeaveDay && !isHalfDayLeave}
                      onChange={() => {
                        setIsSingleLeaveDay(true);
                        setIsHalfDayLeave(false);
                        setFieldValue("is_half_day", false);
                        setFieldValue("end_date", values.start_date);
                      }}
                    />
                    Single Day
                  </label>

                  {/* Multiple Days */}
                  <label>
                    <input
                      type="radio"
                      name="leaveType"
                      className="mr-2 text-secondary"
                      checked={!isSingleLeaveDay && !isHalfDayLeave}
                      onChange={() => {
                        setIsSingleLeaveDay(false);
                        setIsHalfDayLeave(false);
                        setFieldValue("is_half_day", false);
                      }}
                    />
                    Multiple Days
                  </label>
                </div>
                <div className="flex justify-between w-full gap-4">
                  {/* start date */}
                  <div className="w-[50%]">
                    <label className="text-sm font-semibold text-secondary mb-1 block">
                      {"Start date"}
                    </label>
                    <Field
                      type="date"
                      name="start_date"
                      onChange={(e) => {
                        const value = e.target.value || today;
                        setFieldValue("start_date", value);
                        if (isSingleLeaveDay) {
                          setFieldValue("end_date", value);
                        }

                        setFormData((prev) => ({
                          ...prev,
                          start_date: value,
                          end_date: isSingleLeaveDay ? value : prev.end_date,
                        }));
                      }}
                      className={commonInput}
                      disabled={isSubmitting}
                    />

                    <ErrorMessage
                      name="start_date"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                  <div className="w-[50%]">
                    <label className="text-sm font-semibold text-secondary mb-1 block">
                      {"End date"}
                    </label>
                    <Field
                      type="date"
                      name="end_date"
                      onChange={(e) => {
                        setFieldValue("end_date", e.target.value);
                        setFormData((prev) => ({
                          ...prev,
                          end_date: e.target.value,
                        }));
                      }}
                      className={`${commonInput} ${
                        isSingleLeaveDay ? "bg-gray-100 cursor-not-allowed" : ""
                      }`}
                      disabled={isSubmitting || isSingleLeaveDay}
                    />
                    <ErrorMessage
                      name="end_date"
                      component="div"
                      className="text-red-500 text-sm mt-1"
                    />
                  </div>
                </div>

                <div className="">
                  <label className="text-sm font-semibold text-secondary mb-1 block">
                    {"Reason"}
                  </label>
                  <Field
                    type="text"
                    name="reason"
                    placeholder="Enter the reason..."
                    className={commonInput}
                    disabled={isSubmitting}
                  />
                  <ErrorMessage
                    name="reason"
                    component="div"
                    className="text-red-500 text-sm mt-1"
                  />
                </div>
              </>
            )}
            {/* View Mode */}
            {isViewMode && (
              <>
                <div>
                  <FloatingInput
                    label={"Category"}
                    name="category"
                    className=""
                    value={toProperCase(
                      initialValues.category + " Leave" || "-",
                    )}
                    disabled
                  />
                </div>
                <div className="flex gap-6 mt-2">
                  {/* Half Day */}
                  <label className="flex items-center text-secondary">
                    <input
                      type="radio"
                      name="leaveTypeView"
                      className="mr-2"
                      checked={isSingle && isHalf}
                      disabled
                    />
                    Half Day
                  </label>

                  {/* Single Day */}
                  <label className="flex items-center text-secondary">
                    <input
                      type="radio"
                      name="leaveTypeView"
                      className="mr-2"
                      checked={isSingle && !isHalf}
                      disabled
                    />
                    Single Day
                  </label>

                  {/* Multiple Days */}
                  <label className="flex items-center text-secondary">
                    <input
                      type="radio"
                      name="leaveTypeView"
                      className="mr-2"
                      checked={!isSingle}
                      disabled
                    />
                    Multiple Days
                  </label>
                </div>
                <div className="w-full flex justify-between gap-4">
                  <div className="w-[50%]">
                    <FloatingInput
                      label={"Start Date"}
                      name="start_date"
                      value={formatDateDDMMYYYY(initialValues.start_date)}
                      disabled
                    />
                  </div>
                  <div className="w-[50%]">
                    <FloatingInput
                      label={"End Date"}
                      name="end_date"
                      value={formatDateDDMMYYYY(initialValues.end_date)}
                      disabled
                    />
                  </div>
                </div>

                <div>
                  <FloatingInput
                    label={"Reason"}
                    name="reason"
                    value={toProperCase(initialValues.reason || "-")}
                    disabled
                  />
                </div>

                <div className="w-full flex justify-between gap-4">
                  <div className="w-[50%]">
                    <FloatingInput
                      label={"Status"}
                      name="status"
                      value={toProperCase(initialValues.status || "-")}
                      disabled
                    />
                  </div>

                  <div className="w-[50%]">
                    <FloatingInput
                      label={"Leave Applied On"}
                      name="created"
                      value={
                        initialValues?.created
                          ? new Date(initialValues.created).toLocaleString(
                              "en-IN",
                              {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              },
                            )
                          : "-"
                      }
                      disabled
                    />
                  </div>
                </div>
              </>
            )}

            {/* Buttons */}
            {!isViewMode && (
              <div className="flex justify-between gap-3 mt-4">
                <button
                  type="button"
                  onClick={() => navigate(fromPage)}
                  className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-lg font-semibold text-base"
                  disabled={isSubmitting}
                >
                  {"Cancel"}
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !isValid || !dirty}
                  className={`px-3 py-1 rounded-lg text-white transition-all duration-200
    ${
      isSubmitting || !isValid || !dirty
        ? "bg-gray-400 cursor-not-allowed opacity-70"
        : "bg-primary hover:bg-secondary cursor-pointer"
    }`}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </button>
              </div>
            )}
            {isViewMode && (
              <div className="flex justify-end gap-4 mt-6">
                {/* Reject Button (only on approvals page) */}
                {fromPage === routes.LEAVE_APPROVALS && (
                  <button
                    type="button"
                    className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded-lg"
                    disabled={
                      initialValues.status !==
                      LEAVE_REQUEST_STATUS.PENDING_L1_APPROVAL
                    }
                    onClick={() =>
                      handleStatusChange(LEAVE_REQUEST_STATUS.REJECTED)
                    }
                  >
                    Reject
                  </button>
                )}

                {/* Approve Button (only on approvals page) */}
                {fromPage === routes.LEAVE_APPROVALS && (
                  <button
                    type="button"
                    className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-lg"
                    onClick={() =>
                      handleStatusChange(
                        initialValues.status ===
                          LEAVE_REQUEST_STATUS.PENDING_L1_APPROVAL
                          ? LEAVE_REQUEST_STATUS.PENDING_L2_APPROVAL
                          : LEAVE_REQUEST_STATUS.APPROVED,
                      )
                    }
                  >
                    Approve
                  </button>
                )}

                {/* Cancel Button (only on personal leave view) */}
                {fromPage === routes.LEAVE_REQUEST_LIST && (
                  <button
                    type="button"
                    className={`${
                      initialValues.status ===
                        LEAVE_REQUEST_STATUS.PENDING_L1_APPROVAL &&
                      initialValues.user === user.id
                        ? "bg-red-500 hover:bg-red-600"
                        : " bg-gray-500 hover:bg-gray-700 cursor-not-allowed"
                    } text-white px-3 py-1 rounded-lg`}
                    disabled={
                      initialValues.status ===
                        LEAVE_REQUEST_STATUS.PENDING_L1_APPROVAL &&
                      initialValues.user !== user.id
                    }
                    onClick={() => {
                      if (
                        window.confirm(
                          "Are you sure you want to cancel this leave request?",
                        )
                      ) {
                        handleStatusChange(LEAVE_REQUEST_STATUS.CANCELLED);
                      }
                    }}
                  >
                    Cancel Leave
                  </button>
                )}
              </div>
            )}
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default LeaveRequestActions;
